/**
 * Screen capture (web only) — snapshots the driver app's actual rendered screen
 * with html2canvas (loaded from CDN, no bundle cost) and uploads a downscaled JPEG
 * so admin can see the literal screen the driver is on. Best-effort; failures ignored.
 */
import { Platform } from 'react-native';
import { api } from './api';

let h2cPromise: Promise<any> | null = null;
function loadHtml2Canvas(): Promise<any> {
  const w: any = typeof window !== 'undefined' ? window : null;
  if (!w) return Promise.reject(new Error('no window'));
  if (w.html2canvas) return Promise.resolve(w.html2canvas);
  if (h2cPromise) return h2cPromise;
  h2cPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
    s.onload = () => resolve(w.html2canvas);
    s.onerror = () => { h2cPromise = null; reject(new Error('load failed')); };
    document.head.appendChild(s);
  });
  return h2cPromise;
}

function downscale(canvas: HTMLCanvasElement, maxW: number): HTMLCanvasElement {
  const scale = Math.min(1, maxW / canvas.width);
  if (scale === 1) return canvas;
  const c = document.createElement('canvas');
  c.width = Math.round(canvas.width * scale);
  c.height = Math.round(canvas.height * scale);
  const ctx = c.getContext('2d');
  if (ctx) {
    ctx.imageSmoothingEnabled = true;
    (ctx as any).imageSmoothingQuality = 'high';
    ctx.drawImage(canvas, 0, 0, c.width, c.height);
  }
  return c;
}

// Never let a single async step hang forever. html2canvas can stall on a heavy DOM (e.g. the live
// map) and the upload can stall on a bad cell connection — without a cap, `capturing` latches true
// and the awaited step never returns, which froze the driver's app. Timeout => reject => finally
// clears the flag and the next cycle can retry.
function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  return Promise.race([
    p,
    new Promise<T>((_, rej) => setTimeout(() => rej(new Error(`${label} timeout`)), ms)),
  ]);
}

let capturing = false;
export async function captureAndUpload(): Promise<void> {
  if (Platform.OS !== 'web' || typeof document === 'undefined') return;
  if (capturing) return;
  if (document.visibilityState && document.visibilityState !== 'visible') return;
  capturing = true;
  try {
    const h2c = await withTimeout(loadHtml2Canvas(), 8000, 'h2c-load');
    const el = document.getElementById('root') || document.body;
    if (!el) return;
    // Let the screen settle so we snapshot LOADED content, not the loading skeleton
    // (the #1 reason the admin mirror showed empty placeholder boxes). Bail if the driver
    // navigated away / backgrounded during the wait.
    await new Promise((r) => setTimeout(r, 800));
    if (document.visibilityState && document.visibilityState !== 'visible') return;
    // Capture only the VISIBLE viewport (a true screenshot) so it matches the phone
    // proportions — not the whole scrollable page. useCORS (no allowTaint) so
    // cross-origin images don't taint the canvas and break toDataURL.
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // Capture at scale 1 (CSS pixels). The previous 2–3× oversampling made
    // html2canvas rasterize a huge canvas on the main thread, freezing the driver's
    // phone for a beat every cycle. scale 1 is plenty once downscaled to 760px, and
    // keeps each capture cheap enough to not jank the UI.
    const canvas = await withTimeout<any>(h2c(el, {
      scale: 1,
      logging: false,
      useCORS: true,
      backgroundColor: '#ffffff',
      width: vw,
      height: vh,
      x: window.scrollX || 0,
      y: window.scrollY || 0,
      windowWidth: vw,
      windowHeight: vh,
    }), 6000, 'h2c-render');
    const dataUrl = downscale(canvas, 760).toDataURL('image/jpeg', 0.85);
    if (dataUrl && dataUrl.length < 5_500_000) {
      await withTimeout(api.post('/drivers/me/screenshot', { image: dataUrl }), 8000, 'upload');
    }
  } catch {
    /* ignore — capture is best-effort */
  } finally {
    capturing = false;
  }
}
